import type {
  SearchFilterValue,
  SearchHitAnchor,
  SearchRecentSnapshot,
} from '@hibro/shared';
import { pruneSearchFilterValues } from '@hibro/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  mergeResults,
  type ProviderOutcome,
  runSearch,
  type SearchSectionResults,
} from './run-search';
import {
  ALL_TAB_SECTIONS,
  COMMAND_PREFIX,
  parseSearchQuery,
  recentIdentityOf,
  recentSnapshotOf,
  type SearchProvider,
  type SearchResult,
  type SearchSection,
  type SearchTab,
} from './search-contract';

/** Keystroke settling time before providers run. */
const DEBOUNCE_MS = 120;

export interface UseSearchOptions {
  providers: readonly SearchProvider[];
  /** Which tab is active — `all`, or one app's own tab. */
  tab: SearchTab;
  /**
   * Section ids in display order for {@link tab}. Omit to keep the platform
   * constants (`recent` / `command`) first and leave module sections in
   * arrival order — the face that owns the declarations should pass
   * `sectionOrderForTab(...)` so a `scope: "both"` section can sit in two
   * different seats.
   */
  sectionOrder?: readonly SearchSection[];
  /** Per-provider row cap for this face. Omit for "everything". */
  limit?: number;
  /** Whether to search at all — the header only queries while its popover is open. */
  active: boolean;
  /**
   * A filter panel is open and the user is still working in it.
   *
   * Filter edits then accumulate without querying, and land as one search when
   * the panel closes. A picker is ticked one row at a time, so querying per
   * tick means the result list reshuffles under the panel the user is still
   * reading — several round trips to show an intermediate selection nobody
   * asked about.
   */
  editingFilters?: boolean;
  /**
   * Records a pick as a recent search. `snapshot` is what the row looked like,
   * kept for the rows whose provider cannot resolve them back
   * (见 `SearchRecentEntry`).
   */
  onRecord?: (
    providerId: string,
    key: string,
    snapshot?: SearchRecentSnapshot,
  ) => void;
  /**
   * Runs the chosen row's action. `anchor` is where the row was on screen, for
   * an `inPlace` action that shows something beside it (见 `SearchHitAnchor`);
   * absent when the face could not measure the row.
   */
  onRun: (result: SearchResult, anchor?: SearchHitAnchor) => void;
}

export interface UseSearchState {
  query: string;
  setQuery: (next: string) => void;
  /**
   * Filter values set on the active tab, keyed by the declared filter id.
   *
   * What the chips and panels render, which is the **draft**: while a panel is
   * open these run ahead of what the results answer to (见 `editingFilters`).
   */
  filters: Readonly<Record<string, SearchFilterValue>>;
  /**
   * Set one filter's value, or clear it by passing `null`. Re-runs the search
   * once the panel closes, not on the edit itself (见 `editingFilters`).
   */
  setFilter: (id: string, value: SearchFilterValue | null) => void;
  /** Clear every filter (tab switch, close). */
  clearFilters: () => void;
  /** Sections in display order, each already ordered but **not** truncated. */
  sections: SearchSectionResults[];
  /** True while the run is still waiting on a provider (bounded by PROVIDER_WAIT_MS). */
  searching: boolean;
  /** True once the run released and nothing matched. Late rows can still clear this. */
  empty: boolean;
  /** Set when the box is in command mode (input opens with `/`). */
  commandMode: boolean;
  /** The typed command, prefix stripped — for the unknown-command state. */
  typedCommand: string;
  /** Terms the face highlights in a row's title and subtitle. */
  terms: readonly string[];
  /** Record the pick, then run it. `anchor` rides along for an `inPlace` action. */
  select: (result: SearchResult, anchor?: SearchHitAnchor) => void;
  reset: () => void;
}

/**
 * Global-search state: input, debounce, incremental provider results, section
 * merge, and recording a pick.
 *
 * Lives in a hook rather than inside the header component because the header is
 * not the only intended face — the "view all commands" panel (需求 §5.1) will
 * run the same providers with no row cap and must record picks the same way.
 * Keeping this here is what makes that panel markup-only.
 */
export function useSearch(options: UseSearchOptions): UseSearchState {
  const {
    providers,
    tab,
    sectionOrder,
    limit,
    active,
    editingFilters = false,
    onRecord,
    onRun,
  } = options;
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<
    Readonly<Record<string, SearchFilterValue>>
  >({});
  /**
   * The filter values the providers were actually asked with.
   *
   * The query is built from these rather than from `filters` so that `parsed`
   * and the rows on screen always describe the *same* search. Skipping the
   * provider effect while a panel is open would defer the query just as well,
   * but it would leave `parsed` running ahead of the results, and everything
   * derived from it (`empty`, `terms`) describing a search nobody ran.
   */
  const [appliedFilters, setAppliedFilters] = useState<
    Readonly<Record<string, SearchFilterValue>>
  >({});
  const [outcomes, setOutcomes] = useState<readonly ProviderOutcome[]>([]);
  const [searching, setSearching] = useState(false);
  // Distinguishes "no hits" from "not asked yet": the empty state may only be
  // shown once every provider has settled for the *current* query.
  const [settledFor, setSettledFor] = useState<string | null>(null);
  // Identity of `providers` churns every workbench render (whenContext /
  // commands / recents). The effect must not restart on that or `searching`
  // never lands and the placeholder never yields to empty.
  const providersRef = useRef(providers);
  providersRef.current = providers;

  /**
   * Promote the draft once the user is done editing.
   *
   * An effect rather than a `commit()` the face calls when it closes a panel:
   * a date preset sets a value and closes the panel in one handler, so an
   * imperative commit would read the pre-change value out of its closure. By
   * the time an effect runs, React has committed both.
   */
  useEffect(() => {
    if (editingFilters) {
      return;
    }
    setAppliedFilters((applied) =>
      sameFilterMap(applied, filters) ? applied : filters,
    );
  }, [editingFilters, filters]);

  // A filter the user opened and set nothing in must not read as a constraint
  // downstream (见 `pruneSearchFilterValues`), and pruning here rather than at
  // the widget keeps "cleared" and "never touched" the same thing.
  const activeFilters = useMemo(
    () => pruneSearchFilterValues(appliedFilters),
    [appliedFilters],
  );

  const parsed = useMemo(
    () => parseSearchQuery(query, tab, limit, activeFilters),
    [query, tab, limit, activeFilters],
  );

  // Latest run only: a stale provider resolving after the user typed again must
  // not repopulate the list.
  const runIdRef = useRef(0);

  useEffect(() => {
    if (!active) {
      setOutcomes([]);
      setSearching(false);
      setSettledFor(null);
      return;
    }
    const runId = (runIdRef.current += 1);
    const controller = new AbortController();
    const collected: ProviderOutcome[] = [];
    // Hold the placeholder for the whole run, not just until the first local
    // provider returns [] — modules on the All tab arrive later, and flipping
    // to empty on the first reply squeezes Searching out (then the list pops in).
    setSearching(true);

    const timer = setTimeout(() => {
      const currentProviders = providersRef.current;
      void runSearch(currentProviders, parsed, {
        signal: controller.signal,
        onResults: (providerId, results) => {
          if (runIdRef.current !== runId) {
            return;
          }
          const provider = currentProviders.find(
            (entry) => entry.id === providerId,
          );
          collected.push({
            providerId,
            rank: provider?.rank ?? 0,
            section: provider?.section ?? '',
            results,
          });
          setOutcomes([...collected]);
        },
        onError: (providerId, error) => {
          if (runIdRef.current !== runId) {
            return;
          }
          const provider = currentProviders.find(
            (entry) => entry.id === providerId,
          );
          collected.push({
            providerId,
            rank: provider?.rank ?? 0,
            section: provider?.section ?? '',
            error,
          });
          setOutcomes([...collected]);
        },
      }).finally(() => {
        if (runIdRef.current === runId) {
          setSearching(false);
          setSettledFor(query);
        }
      });
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [active, parsed, query]);

  const sections = useMemo(
    () => mergeResults(outcomes, sectionOrder ?? ALL_TAB_SECTIONS),
    [outcomes, sectionOrder],
  );

  const select = useCallback(
    (result: SearchResult, anchor?: SearchHitAnchor) => {
      // Recorded here rather than in the face: every face records the same way,
      // and "recent" means "searched and went into", which is exactly this call.
      const identity = recentIdentityOf(result);
      onRecord?.(identity.providerId, identity.key, recentSnapshotOf(result));
      onRun(result, anchor);
    },
    [onRecord, onRun],
  );

  /**
   * Write a filter, or clear it with `null`.
   *
   * Returns the **same** state object when nothing actually changed, and that is
   * load-bearing rather than a micro-optimisation: `parsed` is memoised on
   * `filters`, and the effect that runs the providers is memoised on `parsed`.
   * A widget re-emitting an equal value (a picker re-ticking what was already
   * ticked, a calendar re-firing the same range) would otherwise mint a new
   * object and re-query every provider for a value the user did not change.
   */
  const setFilter = useCallback(
    (id: string, value: SearchFilterValue | null) => {
      setFilters((current) => {
        if (value === null) {
          if (!(id in current)) {
            return current;
          }
          const { [id]: _removed, ...rest } = current;
          return rest;
        }
        if (sameFilterValue(current[id], value)) {
          return current;
        }
        return { ...current, [id]: value };
      });
    },
    [],
  );

  // Both clear the applied side outright rather than letting the commit effect
  // carry it over: they mean "these constraints are gone now", and routing that
  // through the draft would make it wait on a panel that may still be open.
  const clearFilters = useCallback(() => {
    setFilters(emptyIfNeeded);
    setAppliedFilters(emptyIfNeeded);
  }, []);

  const reset = useCallback(() => {
    setQuery('');
    setFilters(emptyIfNeeded);
    setAppliedFilters(emptyIfNeeded);
    setOutcomes([]);
    setSettledFor(null);
  }, []);

  const hasRows = sections.some((section) => section.results.length > 0);
  return {
    query,
    setQuery,
    filters,
    setFilter,
    clearFilters,
    sections,
    searching,
    empty: !hasRows && settledFor === query,
    commandMode: parsed.prefix === COMMAND_PREFIX,
    typedCommand:
      parsed.prefix === COMMAND_PREFIX
        ? query.trimStart().slice(COMMAND_PREFIX.length)
        : '',
    terms: parsed.terms,
    select,
    reset,
  };
}

/** Identity-preserving "make this empty", so an already-empty map re-renders nothing. */
function emptyIfNeeded(
  current: Readonly<Record<string, SearchFilterValue>>,
): Readonly<Record<string, SearchFilterValue>> {
  return Object.keys(current).length === 0 ? current : {};
}

/** Whether two maps express the same set of constraints (见 `sameFilterValue`). */
function sameFilterMap(
  a: Readonly<Record<string, SearchFilterValue>>,
  b: Readonly<Record<string, SearchFilterValue>>,
): boolean {
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) {
    return false;
  }
  return keys.every((key) => {
    const next = b[key];
    return next !== undefined && sameFilterValue(a[key], next);
  });
}

/** Whether two filter values express the same constraint (见 `setFilter`). */
function sameFilterValue(
  a: SearchFilterValue | undefined,
  b: SearchFilterValue,
): boolean {
  if (a === undefined || a.type !== b.type) {
    return false;
  }
  if (a.type === 'picker' && b.type === 'picker') {
    // Order is the picker's presentation, not part of the constraint.
    return (
      a.ids.length === b.ids.length && a.ids.every((id) => b.ids.includes(id))
    );
  }
  if (a.type === 'dateRange' && b.type === 'dateRange') {
    return a.from === b.from && a.to === b.to;
  }
  return false;
}
